import type { GameSnapshot } from "../core/game";
import type { Maze } from "../core/maze";
import type { GhostName } from "../core/types";

/**
 * A translucent plan view of the whole deck, top right.
 *
 * The isometric camera is deliberately tight, so at any moment you can see
 * only a few tiles around the astronaut. That reads well but leaves you blind
 * to where the slop are and which wings still hold Story Points. The minimap
 * buys that back without pulling the camera out again.
 *
 * It is a 2D canvas rather than a second WebGL pass: the static hull is drawn
 * once into an offscreen layer and only the moving parts are repainted, so the
 * whole overlay costs a blit and a handful of arcs per frame.
 */

const COLOURS = {
  hull: "rgba(92, 122, 158, 0.55)",
  deck: "rgba(16, 28, 44, 0.55)",
  gate: "rgba(255, 79, 154, 0.9)",
  storyPoint: "rgba(143, 233, 255, 0.85)",
  quotaReset: "rgba(255, 199, 90, 0.95)",
  player: "#ffffff",
  frightened: "#3b4cff",
  eaten: "rgba(160, 180, 210, 0.45)",
} as const;

const SLOP_COLOURS: Readonly<Record<GhostName, string>> = {
  blinky: "#ff5a5a",
  pinky: "#ff8ad4",
  inky: "#5fe0ff",
  clyde: "#ffb14d",
};

export class Minimap {
  readonly canvas: HTMLCanvasElement;

  private readonly ctx: CanvasRenderingContext2D;
  private readonly base: HTMLCanvasElement;
  private readonly cell: number;
  private readonly dpr: number;

  constructor(
    private readonly maze: Maze,
    private readonly host: HTMLElement,
    cellPixels = 6,
  ) {
    this.dpr = Math.min(window.devicePixelRatio || 1, 2);
    this.cell = cellPixels;

    const width = maze.width * this.cell;
    const height = maze.height * this.cell;

    this.canvas = document.createElement("canvas");
    this.canvas.className = "minimap";
    this.canvas.dataset["testid"] = "minimap";
    this.canvas.width = width * this.dpr;
    this.canvas.height = height * this.dpr;
    this.canvas.style.width = `${width}px`;
    this.canvas.style.height = `${height}px`;
    host.appendChild(this.canvas);

    const ctx = this.canvas.getContext("2d");
    if (ctx === null) throw new Error("minimap: 2D canvas context unavailable");
    this.ctx = ctx;

    this.base = document.createElement("canvas");
    this.base.width = this.canvas.width;
    this.base.height = this.canvas.height;
    this.paintHull();
  }

  /** The hull never changes, so it is rasterised once. */
  private paintHull(): void {
    const ctx = this.base.getContext("2d");
    if (ctx === null) throw new Error("minimap: offscreen 2D context unavailable");
    const s = this.cell * this.dpr;
    ctx.clearRect(0, 0, this.base.width, this.base.height);
    for (let y = 0; y < this.maze.height; y += 1) {
      for (let x = 0; x < this.maze.width; x += 1) {
        const kind = this.maze.kindAt({ x, y });
        if (kind === "wall") ctx.fillStyle = COLOURS.hull;
        else if (kind === "door") ctx.fillStyle = COLOURS.gate;
        else ctx.fillStyle = COLOURS.deck;
        ctx.fillRect(x * s, y * s, s, s);
      }
    }
  }

  private dot(x: number, y: number, radius: number, fill: string): void {
    const s = this.cell * this.dpr;
    this.ctx.beginPath();
    this.ctx.arc(x * s + s / 2, y * s + s / 2, radius * this.dpr, 0, Math.PI * 2);
    this.ctx.fillStyle = fill;
    this.ctx.fill();
  }

  update(snap: GameSnapshot, pellets: { storyPoints: readonly { x: number; y: number }[]; quotaResets: readonly { x: number; y: number }[] }): void {
    const ctx = this.ctx;
    ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
    ctx.drawImage(this.base, 0, 0);

    for (const tile of pellets.storyPoints) this.dot(tile.x, tile.y, 0.9, COLOURS.storyPoint);
    for (const tile of pellets.quotaResets) this.dot(tile.x, tile.y, 2.2, COLOURS.quotaReset);

    for (const ghost of snap.ghosts) {
      const fill =
        ghost.state === "eaten" ? COLOURS.eaten : ghost.frightened ? COLOURS.frightened : SLOP_COLOURS[ghost.name];
      this.dot(ghost.x, ghost.y, 2.6, fill);
    }

    // The astronaut last and largest, with a ring, so it is never lost among
    // four slop markers of similar size.
    const s = this.cell * this.dpr;
    ctx.beginPath();
    ctx.arc(snap.player.x * s + s / 2, snap.player.y * s + s / 2, 3.4 * this.dpr, 0, Math.PI * 2);
    ctx.fillStyle = COLOURS.player;
    ctx.fill();
    ctx.lineWidth = 1.4 * this.dpr;
    ctx.strokeStyle = snap.mode === "frightened" ? COLOURS.quotaReset : "rgba(0,0,0,0.65)";
    ctx.stroke();
  }

  dispose(): void {
    this.host.removeChild(this.canvas);
  }
}
